import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { CampaignMeasureStore } from '../../../../../../stores/campaign-measure.store';
import { CampaignStore } from '../../../../../../stores/campaign.store';
import { Campaign, CampaignStatus } from '../../../../../../types/campaign.interface';
import { getCampaignStatusLabel, CAMPAIGN_STATUS_SEVERITY } from '../../../../../../types/campaign.utils';
import { Severity } from '../../../../../../types/general-types';
import { CampaignAuscultationService, PendingFileOperation } from './campaign-auscultation.service';
import { CampaignHistoryService, CampaignActionContext, AuscultationModificationDetail } from './campaign-history.service';
import { InfoType } from '../../../../../../types/info.interface';
import { TabType } from '../campaign-tab.utils';

type LoadingType = 'none' | 'general' | 'measures' | 'auscultation' | 'history' | 'all';

const firstNonEmpty = (...values: ReadonlyArray<string | null | undefined>): string | undefined => {
  for (const value of values) {
    if (value && value.length > 0) return value;
  }
  return undefined;
};

/** A history entry tied to the pending edit it comes from, so only saved edits are logged. */
interface TracedModification {
  campaignInfoId: number;
  operation?: PendingFileOperation;
  detail: AuscultationModificationDetail;
}

interface PreloadState {
  isPreloading: boolean;
  currentPreloadTab: TabType | null;
  abortController: AbortController | null;
}

@Injectable({ providedIn: 'root' })
export class CampaignPageStore {
  private readonly campaignStore = inject(CampaignStore);
  private readonly campaignMeasureStore = inject(CampaignMeasureStore);
  private readonly auscultationService = inject(CampaignAuscultationService);
  private readonly historyService = inject(CampaignHistoryService);
  private readonly translate = inject(TranslocoService);
  private readonly translations = toSignal(this.translate.selectTranslation());

  private readonly _campaignId = signal<number | null>(null);
  private readonly _assetId = signal<number | null>(null);
  private readonly _loading = signal<LoadingType>('none');
  private readonly _initialLoadDone = signal(false);
  private readonly _measuresLoaded = signal(false);
  private readonly _auscultationLoaded = signal(false);
  private readonly _historyLoaded = signal(false);
  private readonly _activeTab = signal<TabType>('general');

  private preloadState: PreloadState = {
    isPreloading: false,
    currentPreloadTab: null,
    abortController: null,
  };

  readonly campaignId = this._campaignId.asReadonly();
  readonly assetId = this._assetId.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly activeTab = this._activeTab.asReadonly();

  readonly currentCampaign = computed(() => {
    const id = this._campaignId();
    return id ? (this.campaignStore.byId()(id) ?? null) : null;
  });

  readonly isLoading = computed(() => this._loading() !== 'none');
  readonly canShowComponents = computed(() => this._initialLoadDone() && this._loading() !== 'all');
  readonly canShowMeasures = computed(() => this._measuresLoaded() || this._loading() === 'measures');
  readonly canShowAuscultation = computed(() => this._auscultationLoaded() || this._loading() === 'auscultation');
  readonly canShowHistory = computed(() => this._historyLoaded() || this._loading() === 'history');

  readonly isMeasuresReady = this._measuresLoaded.asReadonly();
  readonly isAuscultationReady = this._auscultationLoaded.asReadonly();
  readonly isHistoryReady = this._historyLoaded.asReadonly();

  readonly statusLabel = computed(() => {
    this.translations();
    const campaign = this.currentCampaign();
    return campaign?.status
      ? getCampaignStatusLabel(campaign.status, this.translate)
      : this.translate.translate('entities.campaign.status.unknown');
  });

  readonly statusSeverity = computed<Severity>(() => {
    const campaign = this.currentCampaign();
    return campaign?.status ? (CAMPAIGN_STATUS_SEVERITY[campaign.status] ?? 'info') : 'info';
  });

  readonly currentCampaignTitle = computed(() => {
    const campaign = this.currentCampaign();
    if (!campaign) return '';
    if (campaign.timestamp) {
      return new Date(campaign.timestamp).toLocaleString('fr-CH', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    return this.translate.translate('entities.campaign.fallbackName', { id: campaign.id });
  });

  readonly campaignInfos = this.auscultationService.campaignInfos;
  readonly auscultationValues = this.auscultationService.auscultationValues;

  readonly hasUnsavedMeasures = computed(() => this.campaignMeasureStore.hasPendingChanges());
  readonly hasUnsavedAuscultation = this.auscultationService.hasGlobalChanges;
  readonly hasUnsavedChanges = computed(() => this.hasUnsavedMeasures() || this.hasUnsavedAuscultation());

  constructor() {
    effect(() => {
      const campaignId = this._campaignId();
      if (campaignId) {
        this.historyService.setCampaignId(campaignId);
      }
    });
  }

  setCampaignId(id: number | null): void {
    this._campaignId.set(id);
    if (id) {
      this.auscultationService.setCampaignId(id);
    }
  }

  setAssetId(id: number | null): void {
    this._assetId.set(id);
    if (id) {
      this.auscultationService.setAssetId(id);
    }
  }

  async loadCampaignData(campaignId: number, initialTab: TabType = 'general'): Promise<void> {
    this.abortPreload();
    this._loading.set('all');
    this._campaignId.set(campaignId);
    this._activeTab.set(initialTab);
    this._measuresLoaded.set(false);
    this._auscultationLoaded.set(false);
    this._historyLoaded.set(false);
    this.campaignMeasureStore.resetForNavigation();
    this.campaignMeasureStore.invalidateDetailedCache();

    try {
      const campaign = await this.campaignStore.getByIdAsync(campaignId);
      if (!campaign) throw new Error(`Campaign ${campaignId} not found`);

      this._assetId.set(campaign.assetId);
      this._initialLoadDone.set(true);
      this._loading.set('none');

      await this.loadTabDataWithPriority(initialTab);
      this.startBackgroundPreload(initialTab);
    } catch (error) {
      this._loading.set('none');
      throw error;
    }
  }

  /** Silently re-fetch just the campaign entity (status, pending-conflict / imported flags)
   *  so a live CAMPAIGN_UPDATE refreshes the status dot, tags, menu and the FINISHED read-only
   *  lock — without resetting the active tab or touching in-progress measure/auscultation edits. */
  async refreshCurrentCampaignEntity(): Promise<void> {
    const id = this._campaignId();
    if (id == null) return;
    try {
      await this.campaignStore.loadById(id, { forceRefresh: true });
    } catch {
      // Best-effort live refresh; the next explicit navigation/load reconciles.
    }
  }

  async switchToTab(tab: TabType): Promise<void> {
    if (this._activeTab() === tab) return;

    this._activeTab.set(tab);

    if (this.isTabLoaded(tab)) return;

    this.abortPreload();
    await this.loadTabDataWithPriority(tab);
    this.startBackgroundPreload(tab);
  }

  private isTabLoaded(tab: TabType): boolean {
    switch (tab) {
      case 'general':
        return true;
      case 'measures':
        return this._measuresLoaded();
      case 'auscultation':
        return this._auscultationLoaded();
      case 'history':
        return this._historyLoaded();
    }
  }

  private async loadTabDataWithPriority(tab: TabType): Promise<void> {
    switch (tab) {
      case 'measures':
        await this.loadMeasuresData();
        break;
      case 'auscultation':
        await this.loadAuscultationData();
        break;
      case 'history':
        await this.loadHistoryData();
        break;
    }
  }

  private startBackgroundPreload(excludeTab: TabType): void {
    const tabsToPreload = this.getPreloadOrder(excludeTab);
    if (tabsToPreload.length === 0) return;

    this.preloadState.abortController = new AbortController();
    this.preloadState.isPreloading = true;

    void this.preloadTabsSequentially(tabsToPreload);
  }

  private getPreloadOrder(excludeTab: TabType): TabType[] {
    const allTabs: TabType[] = ['measures', 'auscultation', 'history'];
    return allTabs.filter(tab => tab !== excludeTab && !this.isTabLoaded(tab));
  }

  private async preloadTabsSequentially(tabs: TabType[]): Promise<void> {
    for (const tab of tabs) {
      if (this.preloadState.abortController?.signal.aborted) {
        break;
      }

      if (this.isTabLoaded(tab)) continue;

      this.preloadState.currentPreloadTab = tab;

      try {
        await this.loadTabDataSilent(tab);
      } catch {
        // Silent fail for preload
      }
    }

    this.preloadState.isPreloading = false;
    this.preloadState.currentPreloadTab = null;
  }

  private async loadTabDataSilent(tab: TabType): Promise<void> {
    const campaignId = this._campaignId();
    const assetId = this._assetId();
    if (!campaignId) return;

    switch (tab) {
      case 'measures':
        if (!this._measuresLoaded()) {
          await this.campaignMeasureStore.getByCampaignIdWithDetails(campaignId);
          this._measuresLoaded.set(true);
        }
        break;
      case 'auscultation':
        if (!this._auscultationLoaded() && assetId) {
          await this.auscultationService.loadAuscultationData(campaignId, assetId);
          this._auscultationLoaded.set(true);
        }
        break;
      case 'history':
        if (!this._historyLoaded()) {
          await this.historyService.loadCampaignHistory(campaignId);
          this._historyLoaded.set(true);
        }
        break;
    }
  }

  private abortPreload(): void {
    if (this.preloadState.abortController) {
      this.preloadState.abortController.abort();
      this.preloadState.abortController = null;
    }
    this.preloadState.isPreloading = false;
    this.preloadState.currentPreloadTab = null;
  }

  async loadMeasuresData(forceReload = false): Promise<void> {
    const campaignId = this._campaignId();
    if (!campaignId) return;

    const isInitialLoad = !this._measuresLoaded();
    if (!forceReload && !isInitialLoad) return;

    if (isInitialLoad) this._loading.set('measures');
    try {
      await this.campaignMeasureStore.getByCampaignIdWithDetails(campaignId, { forceRefresh: forceReload });
    } finally {
      this._measuresLoaded.set(true);
      if (isInitialLoad) this._loading.set('none');
    }
  }

  async loadAuscultationData(forceReload = false): Promise<void> {
    const campaignId = this._campaignId();
    const assetId = this._assetId();
    if (!campaignId || !assetId) return;

    const isInitialLoad = !this._auscultationLoaded();
    if (!forceReload && !isInitialLoad) return;

    if (isInitialLoad) this._loading.set('auscultation');
    try {
      await this.auscultationService.loadAuscultationData(campaignId, assetId);
    } finally {
      this._auscultationLoaded.set(true);
      if (isInitialLoad) this._loading.set('none');
    }
  }

  async loadHistoryData(forceReload = false): Promise<void> {
    const campaignId = this._campaignId();
    if (!campaignId) return;

    const isInitialLoad = !this._historyLoaded();
    if (!forceReload && !isInitialLoad) return;

    if (isInitialLoad) this._loading.set('history');
    try {
      await this.historyService.loadCampaignHistory(campaignId);
    } finally {
      this._historyLoaded.set(true);
      if (isInitialLoad) this._loading.set('none');
    }
  }

  async updateCampaignWithHistory(campaignId: number, updates: Partial<Campaign>): Promise<Campaign> {
    const currentCampaign = this.currentCampaign();
    const previousData = currentCampaign ? { ...currentCampaign } : undefined;

    const updatedCampaign = await this.campaignStore.update(campaignId, updates);
    if (!updatedCampaign) throw new Error(`Campaign ${campaignId} update produced no payload`);

    const campaignTimestamp = updatedCampaign.timestamp || currentCampaign?.timestamp;
    const context: CampaignActionContext = {
      campaignId,
      ...(campaignTimestamp && { campaignName: this.historyService.formatLogDate(campaignTimestamp) }),
      ...(updatedCampaign.assetId !== undefined && { assetId: updatedCampaign.assetId }),
    };

    await this.historyService.logCampaignUpdate(context, updates, previousData);
    this.invalidateHistoryCache();

    // The backend re-aligns mandatory measures with the new date (auto-plan/unplan),
    // so a date change must invalidate the cached measures grid to surface the result.
    const newTimestamp = updatedCampaign.timestamp ?? updates.timestamp;
    const previousTimestamp = previousData?.timestamp;
    const timestampChanged =
      newTimestamp != null &&
      (previousTimestamp == null || new Date(newTimestamp).getTime() !== new Date(previousTimestamp).getTime());
    if (timestampChanged) {
      this.invalidateMeasuresCache();
      if (this._activeTab() === 'measures') {
        await this.loadMeasuresData(true);
      }
    }

    return updatedCampaign;
  }

  async updateCampaignStatusWithHistory(campaignId: number, newStatus: CampaignStatus): Promise<Campaign | null> {
    return this.historyService.updateCampaignStatusWithHistory(campaignId, newStatus);
  }

  async deleteCampaignWithHistory(campaignId: number): Promise<boolean> {
    return this.historyService.deleteCampaignWithHistory(campaignId);
  }

  async saveAllAuscultationChangesWithHistory(): Promise<void> {
    const pendingChanges = this.auscultationService.globalPendingChanges();
    const pendingFileOps = this.auscultationService.pendingFileOperations();

    if (pendingChanges.size === 0 && pendingFileOps.size === 0) return;

    // Read the old values before saving: afterwards the page already shows the new ones.
    const modifications = this.extractAuscultationModifications(pendingChanges, pendingFileOps);
    try {
      await this.auscultationService.saveAllChanges();
    } catch (error) {
      // A save can stop halfway; what already reached the server still has to be
      // traced, without hiding the failure the user must see.
      await this.logSavedAuscultationChanges(modifications).catch(() => undefined);
      throw error;
    }
    await this.logSavedAuscultationChanges(modifications);
  }

  private async logSavedAuscultationChanges(modifications: TracedModification[]): Promise<void> {
    const remainingChanges = this.auscultationService.globalPendingChanges();
    const remainingFileOps = this.auscultationService.pendingFileOperations();
    const saved = modifications
      .filter(({ campaignInfoId, operation }) =>
        operation ? !(remainingFileOps.get(campaignInfoId) ?? []).includes(operation) : !remainingChanges.has(campaignInfoId),
      )
      .map(({ detail }) => detail);
    if (saved.length === 0) return;

    const currentCampaign = this.currentCampaign();
    const context: CampaignActionContext = {
      campaignId: this._campaignId() ?? 0,
      ...(currentCampaign?.timestamp && { campaignName: this.historyService.formatLogDate(currentCampaign.timestamp) }),
    };

    await this.historyService.logMultipleAuscultationUpdate(context, saved);
    this.invalidateHistoryCache();
  }

  private extractAuscultationModifications(
    pendingChanges: Map<number, unknown>,
    pendingFileOps: Map<number, PendingFileOperation[]>,
  ): TracedModification[] {
    const modifications: TracedModification[] = [];
    const campaignInfos = this.auscultationService.campaignInfos();
    const auscultationValues = this.auscultationService.auscultationValues();

    const campaignInfoMap = new Map(campaignInfos.map(ci => [ci.id, ci]));
    const auscValueMap = new Map(auscultationValues.map(av => [av.campaignInfo.id, av]));

    for (const [campaignInfoId, newValue] of pendingChanges.entries()) {
      const campaignInfo = campaignInfoMap.get(campaignInfoId);
      if (!campaignInfo) continue;

      const auscValue = auscValueMap.get(campaignInfoId);
      const oldValue = auscValue?.value ?? null;
      const infoType = auscValue?.type ?? InfoType.TEXT;

      if (oldValue === newValue) continue;

      const isChoice = infoType === InfoType.CHOICE;
      const resolveValue = (val: unknown) =>
        isChoice
          ? (this.auscultationService.getChoiceLabelById(val as number | string | null) ?? String(val ?? ''))
          : String(val ?? '');

      const infoName = firstNonEmpty(auscValue?.info?.name, campaignInfo.name) ?? `Info #${campaignInfo.infoId}`;
      modifications.push({
        campaignInfoId,
        detail: {
          infoId: campaignInfo.infoId,
          infoName,
          infoType,
          attribute: isChoice ? 'choice_value' : 'text_value',
          oldValue: resolveValue(oldValue),
          newValue: resolveValue(newValue),
          type: 'update',
        },
      });
    }

    for (const [campaignInfoId, operations] of pendingFileOps.entries()) {
      const campaignInfo = campaignInfoMap.get(campaignInfoId);
      if (!campaignInfo) continue;

      const auscValue = auscValueMap.get(campaignInfoId);
      const isImage = auscValue?.type === InfoType.IMAGE;
      const fileInfoType = isImage ? InfoType.IMAGE : InfoType.FILE;
      const fileAttribute = isImage ? 'image' : 'file';
      const auscInfoName = firstNonEmpty(auscValue?.info?.name, campaignInfo.name) ?? `Info #${campaignInfo.infoId}`;

      for (const op of operations) {
        if (op.type === 'add' && op.fileName) {
          modifications.push({
            campaignInfoId,
            operation: op,
            detail: {
              infoId: campaignInfo.infoId,
              infoName: auscInfoName,
              infoType: fileInfoType,
              attribute: fileAttribute,
              oldValue: null,
              newValue: op.fileName,
              type: 'create',
            },
          });
        } else if (op.type === 'remove' && op.fileDataId) {
          const fileName = firstNonEmpty(op.fileName) ?? `#${op.fileDataId}`;
          modifications.push({
            campaignInfoId,
            operation: op,
            detail: {
              infoId: campaignInfo.infoId,
              infoName: auscInfoName,
              infoType: fileInfoType,
              attribute: fileAttribute,
              oldValue: fileName,
              newValue: null,
              type: 'delete',
            },
          });
        }
      }
    }

    return modifications;
  }

  getStatusLabel(status: CampaignStatus): string {
    return getCampaignStatusLabel(status, this.translate);
  }

  getStatusSeverity(status: CampaignStatus): Severity {
    return CAMPAIGN_STATUS_SEVERITY[status] ?? 'info';
  }

  async executeWithLoading<T>(fn: () => Promise<T>, type: LoadingType = 'general'): Promise<T> {
    this._loading.set(type);
    try {
      return await fn();
    } finally {
      this._loading.set('none');
    }
  }

  invalidateMeasuresCache(): void {
    this._measuresLoaded.set(false);
  }

  invalidateAuscultationCache(): void {
    this._auscultationLoaded.set(false);
  }

  invalidateHistoryCache(): void {
    this._historyLoaded.set(false);
  }

  invalidateAllTabCaches(): void {
    this._measuresLoaded.set(false);
    this._auscultationLoaded.set(false);
    this._historyLoaded.set(false);
  }
}
